import { useUser } from "@clerk/clerk-react";
import { observer } from "mobx-react";
import { useMemo } from "react";
import { useNavigate } from "react-router";
import { BasePage } from "./BasePage";
import { MainStudy } from "./study/MainStudy";
import { CompactStudyStreak, StudyStreakCard } from "./study/StudyStreakCard";
import { StudyUpgradeBanner } from "./study/StudyUpgradeBanner";
import { useRootStore } from "@/state/RootStore";
import { APP_TO_BUILD } from "../../appsConfig";
import { captureEvent } from "@/utils/posthog";
import assetsForApp from "../../appsAssetListing";
import { appTheme } from "@/config/appTheme";
import { getStudyStreak } from "@/utils/studyStreak";
import { useTranslation } from "react-i18next";
import {
  getUpgradeBannerCopy,
  getUpgradeToAllAccessBannerCopy,
} from "@/i18n/studyBannerCopy";

function getGreetingKey(date: Date) {
  const hour = date.getHours();
  if (hour < 12) return "study.greeting_morning" as const;
  if (hour < 18) return "study.greeting_afternoon" as const;
  return "study.greeting_evening" as const;
}

const DakotaTestPrep = observer(() => {
  const store = useRootStore();
  const navigate = useNavigate();
  const { user } = useUser();
  const { t } = useTranslation();
  const firstName = user?.firstName || store.mainAppStore.profileName?.split(/\s+/)[0] || t("study.student_fallback");
  const greetingKey = useMemo(() => getGreetingKey(new Date()), []);
  const partialLevelAccess =
    (APP_TO_BUILD === "plumbing" || APP_TO_BUILD === "electrical") &&
    store.mainAppStore.hasStudyAccess(false) &&
    !store.mainAppStore.hasAllLevelStudyAccess();
  const showUpgrade = !store.mainAppStore.hasStudyAccess(true) || partialLevelAccess;
  const upgradeCopy = partialLevelAccess
    ? getUpgradeToAllAccessBannerCopy(t, APP_TO_BUILD)
    : getUpgradeBannerCopy(t, APP_TO_BUILD);
  const upgradeImage = partialLevelAccess
    ? assetsForApp.upgradeToAllAccess?.unlock || assetsForApp.specificPractice.unlock
    : assetsForApp.specificPractice.unlock;
  const studyStreak = getStudyStreak(
    Array.from(store.testStore.tests.values())
      .filter((test) => test.isFinished)
      .map((test) => (test.endTime ?? test.startTime).valueOf()),
  );

  const openPaywall = () => {
    captureEvent("home_upgrade_clicked");
    navigate("/paywall");
  };

  return (
    <BasePage>
      <main className="dp-page-container flex min-w-0 flex-col gap-6 px-4 pb-12 sm:px-6">
        <div className="flex min-h-[76px] items-center gap-4 xl:gap-6">
          <section className="min-w-0 flex-1">
            <h1 className="truncate text-[28px] font-semibold leading-[38px] tracking-[-0.28px] text-[#0a0c11]">{t(greetingKey)}, {firstName} 👋</h1>
            <p className="mt-1 truncate text-base leading-6 text-[#5b616d]">{t("study.journey_prompt", { journey: appTheme.studyJourney })}</p>
          </section>
          <div className="min-[1200px]:hidden">
            <CompactStudyStreak streakDays={studyStreak.days} />
          </div>
          <div className="hidden w-fit max-w-full min-[1200px]:block">
            <StudyStreakCard streakDays={studyStreak.days} streakEndsToday={studyStreak.endsToday} />
          </div>
        </div>
        <MainStudy
          promotionalBanner={showUpgrade ? (
            <StudyUpgradeBanner
              description={upgradeCopy.description}
              image={upgradeImage}
              onClick={openPaywall}
              title={upgradeCopy.header}
            />
          ) : null}
        />
      </main>
    </BasePage>
  );
});

export default DakotaTestPrep;
